import { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useMutation } from "@apollo/client";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

import { GROCERIES_BACKGROUND_IMAGE, MOBILE_APP } from "../app/constants/constants";
import { MODIFY_RECIPE_DATA } from "../app/graphql/mutations";
import { setInitialRecipeOpen, setRecipeData } from "../app/recipeCreation/createRecipe.slice";
import { isViewRecipeOpenSelector, setRecipe } from "../app/recipePage/recipePage.slice";
import { useAppDispatch, useAppSelector } from "../app/store/hooks";
import RecipeIngredientsSection from "../components/ingredientsPage/recipeIngredient";
import useRatingSummary from "../components/recipePage/hooks/useRatingSummary";
import RecipeBanner from "../components/recipePage/recipeBanner";
import RecipeDescription from "../components/recipePage/recipeDesc";
import RecipePageSkeleton from "../components/recipePage/recipePageSkeleton";
import { formattingRecipeTime } from "../components/recipePage/recipeUtils";
import { DecrementControl, IncrementControl } from "../components/recipePage/servingSizeControls";
import { TextPrimaryBold } from "../components/typography";
import ViewRecipeSteps from "../components/viewRecipe/ViewRecipeSteps";
import useLogRecipeEdit from "../hooks/useLogRecipeEdit";
import { Clock } from "../iconRef/recipePageIcons";
import { ErrorManagementContext } from "../utils/errorManagement/ErrorManagementProvider";
import { EventLogsEnum } from "../utils/events";
import { EditIcon, RightArrow } from "../utils/iconRefs/recipePageIcons";
import { recipeE3TransformerQL2FS } from "../utils/RecipeBackEnd/hasuraFSMigrationTransformers";
import useRecipeFromDB from "../utils/RecipeBackEnd/loadRecipe";
import styles from "./recipePage.module.css";

const RecipePage = () => {
    const dispatch = useAppDispatch()
    const history = useHistory()

    const { cookClubLog } = useContext(ErrorManagementContext)

    const { recipeId: urlRecipeId } = useParams<any>();

    const { loading } = useRecipeFromDB(urlRecipeId);

    const recipe = useAppSelector((state) => state.recipePage.recipe)
    const isViewRecipeOpen = useAppSelector(isViewRecipeOpenSelector)
    const recipeData = useAppSelector((state) => state.recipeCreation.recipeData)
    const initialRecipeOpen = useAppSelector((state) => state.recipeCreation.initialRecipeOpen)

    const [servings, setServings] = useState(recipe?.metadata?.minServing ?? 2)
    const [editServing, setEditServing] = useState(initialRecipeOpen)
    const [image, setImage] = useState<string>(recipe?.metadata?.url ?? '')

    const [modifyRecipeData] = useMutation(MODIFY_RECIPE_DATA)
    const { logRecipeEdit } = useLogRecipeEdit();

    const recipeCreatedSource = recipe?.metadata?.createdSource

    const cookingTime = recipeData?.Recipe_Variations?.find((recipe) => recipe.default === true)?.cooking_time;
    const cookTimeFormatted = cookingTime && formattingRecipeTime(cookingTime);
    const { ratingsSummary, loading: ratingsLoading } = useRatingSummary(urlRecipeId);

    const onView = async () => {
        if (!recipe?.id && !recipeData?.id) {
            cookClubLog(`Something went wrong. Please try again.`, "", {}, { ui: { type: "error" }, browserConsole: true })
            history.push("/");
            return;
        }
        setEditServing(false)
        dispatch(setInitialRecipeOpen(false))
        history.push(`/userCreatedRecipe/${urlRecipeId}/steps`)
        logRecipeEdit(EventLogsEnum.APP_RECIPE_VIEW, {
            recipeId: recipe?.id ?? null
        })
        if (image !== recipe?.metadata?.url || servings !== recipe?.metadata?.minServing) {
            const recipeDataCopy = { ...recipeData, Recipe_Variations: [{ ...recipeData.Recipe_Variations[0], max_serving: servings, min_serving: servings, image_url: image }] }
            try {
                const { data } = await modifyRecipeData({
                    variables: { recipeData: recipeDataCopy, recipeId: recipeData.id }
                })
                dispatch(setRecipeData(data.update_User_Generated_Recipes.returning[0].recipeData))
                dispatch(setRecipe({ recipe: recipeE3TransformerQL2FS(data.update_User_Generated_Recipes.returning[0].recipeData) }))
            }
            catch (err) {
                console.log(err)
                setImage(recipe?.metadata?.url ?? '')
                setServings(recipe?.metadata?.minServing ?? 2)
                cookClubLog(`Could not save. Please try again.`, "", {}, { ui: { type: "info" }, browserConsole: true })
            }
        }
    }

    useEffect(() => {
        setImage(recipe?.metadata?.url ?? '')
        setServings((recipe?.metadata?.minServing ?? 2))
    }, [recipe]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (loading || ratingsLoading)
        return (<RecipePageSkeleton />)

    if (isViewRecipeOpen)
        return (<ViewRecipeSteps />)

    return (
        <Container className={styles.wrapper}>
            <RecipeBanner image={image} recipe={recipe} servings={servings} />
            <Box className={styles.recipe_data_container} sx={{ backgroundImage: `url(${GROCERIES_BACKGROUND_IMAGE})` }}>
                {recipe ? <RecipeDescription ratingsSummary={ratingsSummary} recipe={recipe} /> : null}
                {recipe ? <RecipeIngredientsSection recipe={recipe} /> : null}
            </Box>
        </Container>
    )
}
export default RecipePage
